import { createUUID } from '@shared/constants/Keys';
import { KeyCommandDisplayMapping } from '@shared/models/deviceProfiles/KeyCommandMapping';
import { MacroDisplayCommandModel, MacroDisplayModel } from '@shared/models/deviceProfiles/MacroModel';
import { ProfileConflictItem } from '@shared/models/profiles/ProfileConflictModel';
import { useTranslation } from 'react-i18next';
import CurrentUserContainer from './state/CurrentUserContainer';
import GlobalCustomControlsContainer from './state/GlobalCustomControlsContainer';

interface ICommand {
    macroOrKeyName: string;
    commandDisplayName: string;
    macroOrKeyId: string;
    pageLink: string;
}

export interface ICommandIntersection {
    value: string;
    controlId: string;
    deviceId: string;
    controlName: string;
    profileId: string;
    profileSource: 'macro' | 'key' | 'dictationCommand' | 'generationCommand' | 'global';
    profileName?: string;
}

export default function useGlobalEditProfileConflicts() {
    const { currentUserRef } = CurrentUserContainer.useContainer();
    const { getAllCustomCommandsMap } = GlobalCustomControlsContainer.useContainer();
    const { t } = useTranslation();

    function getGlobalEditProfileConflicts(
        macros: MacroDisplayModel[],
        dictationCommands: string[],
        keyCommands: KeyCommandDisplayMapping[],
        profileId: string
    ) {
        const deviceIntersectionMap: Map<string, ProfileConflictItem[]> = new Map([]);
        const globalIntersections: ProfileConflictItem[] = [];
        let deviceCommandConflictDisplay: ProfileConflictItem[] = [];
        let globalCommandConflictDisplay: ProfileConflictItem[] = [];
        const editProfileCommandsMap = new Map<string, ICommand[]>();

        if (currentUserRef.current?.userPreferences?.isWarningConflictIndicatorsOptOut)
            return { globalCommandConflictDisplay, deviceCommandConflictDisplay };

        const addCommandsToMap = (
            commandsMap: Map<string, ICommand[]>,
            commands: MacroDisplayCommandModel[] | { command: string; commandType: string }[],
            macroOrKeyId: string,
            macroOrKeyName: string,
            pageLink: string
        ) => {
            for (const command of commands) {
                const normalizedCommand = command.command.trim().toLowerCase();
                if (!normalizedCommand) continue;
                if (!commandsMap.has(normalizedCommand)) {
                    commandsMap.set(normalizedCommand, []);
                }
                commandsMap.get(normalizedCommand)!.push({
                    macroOrKeyName: macroOrKeyName ? macroOrKeyName : t('common:noName'),
                    commandDisplayName: command?.command,
                    macroOrKeyId: macroOrKeyId,
                    pageLink,
                });
            }
        };

        if (macros) {
            for (const macro of macros) {
                addCommandsToMap(
                    editProfileCommandsMap,
                    macro.commands,
                    macro.id,
                    macro.name ? macro.name : t('common:noName'),
                    `/profiles/edit/${profileId}`
                );
            }
        }
        if (keyCommands) {
            for (const keyCommand of keyCommands) {
                addCommandsToMap(
                    editProfileCommandsMap,
                    keyCommand.commands,
                    keyCommand.key,
                    `${t('common:keys.title')}: ${keyCommand.displayName}`,
                    `/profiles/edit/${profileId}/advanced/keybindings`
                );
            }
        }
        if (dictationCommands) {
            addCommandsToMap(
                editProfileCommandsMap,
                dictationCommands.map((command) => ({ command, commandType: 'dictationCommand' })),
                'dictation',
                t('common:dictation.title'),
                `/profiles/edit/${profileId}/advanced/dictation`
            );
        }

        const customCommandsMap = getAllCustomCommandsMap();

        // Iterate through custom commands and check for intersections
        for (const [customValue, customDetails] of customCommandsMap) {
            const normalizedCustomValue = customDetails.value.trim().toLowerCase();
            if (editProfileCommandsMap.has(normalizedCustomValue)) {
                const profileDetailsArray = editProfileCommandsMap.get(normalizedCustomValue);
                if (!profileDetailsArray) continue;
                for (const profileDetails of profileDetailsArray) {
                    if (customDetails.source === 'global') {
                        globalIntersections.push({
                            key: createUUID(),
                            scrollID: profileDetails.macroOrKeyId,
                            displayString: `"${normalizedCustomValue}" ${t(
                                'common:customControls.conflictIndicators.usedInput'
                            )} ${customDetails.deviceName}: ${customDetails.name} & ${profileDetails.macroOrKeyName}`,
                            pageLink: profileDetails.pageLink,
                        });
                    } else {
                        if (!deviceIntersectionMap.has(customDetails.source)) {
                            deviceIntersectionMap.set(customDetails.source, []);
                        }
                        deviceIntersectionMap.get(customDetails.source)!.push({
                            key: createUUID(),
                            scrollID: profileDetails.macroOrKeyId,
                            displayString: `"${normalizedCustomValue}" ${t(
                                'common:customControls.conflictIndicators.usedInput'
                            )} ${customDetails.deviceName}: ${customDetails.name} & ${profileDetails.macroOrKeyName} `,
                            pageLink: profileDetails.pageLink,
                        });
                    }
                }
            }
        }

        deviceCommandConflictDisplay = [...deviceIntersectionMap.values()].flat();
        globalCommandConflictDisplay = globalIntersections;

        return { deviceCommandConflictDisplay, globalCommandConflictDisplay };
    }
    return getGlobalEditProfileConflicts;
}
